import { reactive, toRaw } from 'vue';
import { Storage } from '@ionic/storage';
import campaignsData from '../../content/dist/campaigns.json';
import { getAllLevelIds } from './levelManifest';

const campaigns = campaignsData.campaigns;
const PROGRESS_STORAGE_KEY = 'progress';

const getInitialProgress = () => ({
  levelState: {}, // Track per-level state (tutorialShown, failures, bestPerfect, bestAssisted)
  claimedRewards: {}, // Track which campaign rewards have been claimed
});

export default class ProgressManager {
  constructor() {
    this.storage = new Storage();
    this.progress = reactive(getInitialProgress());
  }

  /**
   * Initializes the storage driver and loads progress.
   */
  async init() {
    await this.storage.create();
    await this.loadProgress();
  }

  /**
   * Loads progress from storage into memory.
   */
  async loadProgress() {
    const savedProgress = await this.storage.get(PROGRESS_STORAGE_KEY);
    if (savedProgress) {
      // Migration: Initialize levelState if missing (for backward compatibility)
      if (!savedProgress.levelState) {
        savedProgress.levelState = {};
      }

      // Migration: Initialize claimedRewards if missing (for backward compatibility)
      if (!savedProgress.claimedRewards) {
        savedProgress.claimedRewards = {};
      }

      // Migration: Remove old campaign-based fields if present
      if ('currentCampaignId' in savedProgress) {
        delete savedProgress.currentCampaignId;
        delete savedProgress.currentLevelIndex;
        delete savedProgress.unlockedCampaignIds;
        // Save the migrated format back to storage
        await this.storage.set(PROGRESS_STORAGE_KEY, savedProgress);
      }

      // Merge saved progress to gracefully handle data model changes
      Object.assign(this.progress, savedProgress);
    }
  }

  /**
   * Saves the current progress from memory to storage.
   */
  async saveProgress() {
    await this.storage.set(PROGRESS_STORAGE_KEY, toRaw(this.progress));
  }

  /**
   * Gets the index of the first incomplete level.
   * Returns the total count if all levels are complete.
   * @returns {number} The index of the first incomplete level
   */
  getFirstIncompleteLevelIndex() {
    const allLevelIds = getAllLevelIds();
    for (let i = 0; i < allLevelIds.length; i++) {
      if (!this.isLevelComplete(allLevelIds[i])) {
        return i; // First incomplete
      }
    }
    return allLevelIds.length; // All complete
  }

  /**
   * Checks if a level is unlocked for play.
   * A level is unlocked if its index is <= the first incomplete level index.
   * @param {string} levelId - The level identifier
   * @returns {boolean} True if the level is unlocked
   */
  isLevelUnlocked(levelId) {
    // Find which campaign this level belongs to
    const campaign = campaigns.find(c => c.levelIds?.includes(levelId));
    if (!campaign) {
      // Fallback for sandbox etc
      return true;
    }

    // Check if the campaign itself is unlocked
    if (!this.isCampaignUnlocked(campaign.id)) {
      return false;
    }

    // Within an unlocked campaign, sequential unlock up to first incomplete
    const levelIndex = campaign.levelIds.indexOf(levelId);
    const firstIncompleteInCampaign = campaign.levelIds.findIndex(
      id => !this.isLevelComplete(id)
    );

    if (firstIncompleteInCampaign === -1) {
      // All complete in this campaign
      return true;
    }

    return levelIndex <= firstIncompleteInCampaign;
  }

  /**
   * Gets the next unbeaten level.
   * @returns {string | null} The level ID of the next unbeaten level, or null if all beaten
   */
  getNextUnbeatenLevel() {
    for (const campaign of campaigns) {
      if (!this.isCampaignUnlocked(campaign.id)) continue;
      for (const levelId of (campaign.levelIds || [])) {
        if (!this.isLevelComplete(levelId)) {
          return levelId;
        }
      }
    }
    return null;
  }

  /**
   * Checks if the entire game is complete (all levels beaten).
   * @returns {boolean} True if all levels have been beaten
   */
  isGameComplete() {
    // Check ALL campaigns (not just unlocked ones) to avoid false positives
    // when later campaigns are locked but have unbeaten levels
    for (const campaign of campaigns) {
      for (const levelId of (campaign.levelIds || [])) {
        if (!this.isLevelComplete(levelId)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Checks if a specific campaign is complete (all its levels beaten).
   * @param {string} campaignId - The campaign identifier
   * @returns {boolean} True if all levels in the campaign are beaten
   */
  isCampaignComplete(campaignId) {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return false;

    return campaign.levelIds.every(levelId => this.isLevelComplete(levelId));
  }

  /**
   * Checks if the player has any progress (any level started).
   * @returns {boolean} True if any level has been started
   */
  hasAnyProgress() {
    // Check if any level has meaningful progress (not just default values)
    for (const levelKey in this.progress.levelState) {
      const state = this.progress.levelState[levelKey];
      // Has progress if: completed a level, failed at least once, or saw tutorial
      if (this.isLevelComplete(levelKey) || state.failures > 0 || state.tutorialShown) {
        return true;
      }
    }
    return false;
  }

  /**
   * Marks a campaign reward as claimed and saves.
   * @param {string} campaignId - The campaign identifier
   */
  async markRewardClaimed(campaignId) {
    this.progress.claimedRewards[campaignId] = true;
    await this.saveProgress();
  }

  /**
   * Checks if a campaign reward has been claimed.
   * @param {string} campaignId - The campaign identifier
   * @returns {boolean} True if the reward has been claimed
   */
  isRewardClaimed(campaignId) {
    return !!this.progress.claimedRewards[campaignId];
  }

  /**
   * Checks if a campaign is unlocked for play.
   * The first campaign is always unlocked. Subsequent campaigns require the
   * previous campaign's reward to have been claimed.
   * @param {string} campaignId - The campaign identifier
   * @returns {boolean} True if the campaign is unlocked
   */
  isCampaignUnlocked(campaignId) {
    const campaignIndex = campaigns.findIndex(c => c.id === campaignId);
    if (campaignIndex <= 0) return true; // First campaign always unlocked
    const previousCampaign = campaigns[campaignIndex - 1];
    return this.isRewardClaimed(previousCampaign.id);
  }

  /**
   * Returns the current progress data.
   * @returns {{unlockedCampaignIds: string[], currentCampaignId: string|null, currentLevelIndex: number}}
   */
  getCurrentProgress() {
    return this.progress;
  }

  /**
   * Clears all progress.
   */
  async clearProgress() {
    Object.assign(this.progress, getInitialProgress());
    await this.saveProgress();
  }

  /**
   * Gets the state for a specific level, initializing if needed.
   * @param {string} levelKey - The level identifier (e.g., '1-1')
   * @returns {{ tutorialShown: boolean, failures: number, bestPerfect: {moveCount: number}|null, bestAssisted: {moveCount: number, undoCount: number}|null }}
   */
  getLevelState(levelKey) {
    if (!this.progress.levelState[levelKey]) {
      this.progress.levelState[levelKey] = {
        tutorialShown: false,
        failures: 0,
        bestPerfect: null,    // Best solution with 0 undos
        bestAssisted: null,   // Best solution with undos (moveCount primary, undoCount tiebreaker)
        bestSolution: null,   // Best overall solution data: { moveCount, solution }
        visionViewed: false,  // Whether player used "show me the solution"
      };
    }
    return this.progress.levelState[levelKey];
  }

  /**
   * Checks if the tutorial has been auto-shown for a level.
   * @param {string} levelKey - The level identifier
   * @returns {boolean}
   */
  getTutorialShown(levelKey) {
    return this.getLevelState(levelKey).tutorialShown;
  }

  /**
   * Sets the tutorial shown state for a level.
   * @param {string} levelKey - The level identifier
   * @param {boolean} shown - Whether the tutorial has been shown
   */
  async setTutorialShown(levelKey, shown) {
    this.getLevelState(levelKey).tutorialShown = shown;
    await this.saveProgress();
  }

  /**
   * Checks if the player has viewed the solution vision for a level.
   * @param {string} levelKey - The level identifier
   * @returns {boolean}
   */
  getVisionViewed(levelKey) {
    return this.getLevelState(levelKey).visionViewed;
  }

  /**
   * Records that the player viewed the solution vision for a level.
   * @param {string} levelKey - The level identifier
   */
  async setVisionViewed(levelKey) {
    this.getLevelState(levelKey).visionViewed = true;
    await this.saveProgress();
  }

  /**
   * Gets the failure count for a level.
   * @param {string} levelKey - The level identifier
   * @returns {number}
   */
  getFailureCount(levelKey) {
    return this.getLevelState(levelKey).failures;
  }

  /**
   * Increments the failure count for a level.
   * @param {string} levelKey - The level identifier
   */
  async incrementFailureCount(levelKey) {
    this.getLevelState(levelKey).failures++;
    await this.saveProgress();
  }

  /**
   * Checks if a level has been completed at least once.
   * @param {string} levelKey - The level identifier
   * @returns {boolean} True if level has been completed
   */
  isLevelComplete(levelKey) {
    const levelState = this.getLevelState(levelKey);
    return levelState.bestPerfect !== null || levelState.bestAssisted !== null;
  }

  /**
   * Gets the best (lowest) overall move count for a level.
   * Returns the minimum move count across both perfect and assisted solutions.
   * Used for backward compatibility with current UI.
   * @param {string} levelKey - The level identifier
   * @returns {number | null} The best move count, or null if never completed
   */
  getBestMoveCount(levelKey) {
    const levelState = this.getLevelState(levelKey);

    const perfectCount = levelState.bestPerfect?.moveCount ?? null;
    const assistedCount = levelState.bestAssisted?.moveCount ?? null;

    // Return lowest overall
    if (perfectCount === null) return assistedCount;
    if (assistedCount === null) return perfectCount;
    return Math.min(perfectCount, assistedCount);
  }

  getBestSolution(levelKey) {
    const levelState = this.getLevelState(levelKey);
    return levelState.bestSolution?.solution || null;
  }

  /**
   * Updates the best solution for a level using two-tier scoring.
   * - Solutions with 0 undos compete in the "perfect" category (lowest move count wins)
   * - Solutions with undos compete in the "assisted" category (move count primary, undo count tiebreaker)
   * @param {string} levelKey - The level identifier
   * @param {number} moveCount - The move count from the current completion
   * @param {number} undoCount - The undo count from the current completion
   * @param {Array} [playerSolution] - The action sequence for this completion
   * @returns {Promise<boolean>} True if a new best was set, false otherwise
   */
  async updateBestSolution(levelKey, moveCount, undoCount, playerSolution) {
    const levelState = this.getLevelState(levelKey);
    let changed = false;

    if (undoCount === 0) {
      // Perfect solution - update bestPerfect if this is better
      if (!levelState.bestPerfect || moveCount < levelState.bestPerfect.moveCount) {
        levelState.bestPerfect = { moveCount };
        changed = true;
      }
    } else {
      // Assisted solution - move count primary, undo count tiebreaker
      const current = levelState.bestAssisted;

      if (!current ||
          moveCount < current.moveCount ||
          (moveCount === current.moveCount && undoCount < current.undoCount)) {
        levelState.bestAssisted = { moveCount, undoCount };
        changed = true;
      }
    }

    // Update bestSolution if this has a lower move count (regardless of undo count)
    if (playerSolution && playerSolution.length > 0) {
      if (!levelState.bestSolution || moveCount < levelState.bestSolution.moveCount) {
        levelState.bestSolution = { moveCount, solution: playerSolution };
        changed = true;
      }
    }

    if (changed) {
      await this.saveProgress();
    }

    return changed;
  }

  /**
   * Resets the failure count for a level to 0.
   * @param {string} levelKey - The level identifier
   */
  async resetFailureCount(levelKey) {
    this.getLevelState(levelKey).failures = 0;
    await this.saveProgress();
  }
}
